import { formatBytes } from '@superdispatch/ui-lab';
import accepts from 'attr-accept';
import { ChangeEvent, cloneElement, ReactElement, useRef } from 'react';
import { useUploadMedia } from 'shared/data/MediaServiceAPI';
import { MAX_FILE_SIZE } from 'shared/form/FileField';
import { logWarning } from 'shared/helpers/Logger';

interface MediaUploadWrapperProps {
  accept?: string;
  maxSize?: number;
  onChange?: (url: string) => void;
  onError?: (error: string) => void;
  children: ReactElement<{ onClick?: (event: MouseEvent) => void }>;
}

export function MediaUploadWrapper({
  accept = '.jpeg, .jpg, .png, .heic, .heif, .webp',
  maxSize = MAX_FILE_SIZE,
  onChange,
  onError,
  children,
}: MediaUploadWrapperProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { mutate } = useUploadMedia({
    onSuccess: (url) => onChange?.(url),
    onError: () => {
      onError?.('Failed to upload media file.');
    },
  });

  const handleClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.click();
    }
  };

  const handleFile = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const file = target.files?.[0];
    if (!file) {
      return;
    }

    if (!accepts(file, accept)) {
      onError?.(`File type must be: ${accept}`);
      logWarning('Unsupported Media Type in MediaUploadWrapper', {
        fileType: file.type,
      });
      return;
    }

    if (maxSize && file.size > maxSize) {
      onError?.(`Attachment size should be less than ${formatBytes(maxSize)}`);
      return;
    }

    mutate(file);
  };

  return (
    <>
      <input
        value=""
        ref={inputRef}
        type="file"
        accept=".jpeg, .jpg, .png, .heic, .heif, .webp"
        hidden={true}
        onChange={handleFile}
      />
      {cloneElement(children, {
        ...children.props,
        onClick: (event: MouseEvent) => {
          handleClick();
          children.props.onClick?.(event);
        },
      })}
    </>
  );
}
